import { orgAPI } from "@/api/api";
import { useQuery } from "@tanstack/react-query";

interface UseGranolaMeetingFetchProps {
  integrationConnectionId?: string;
  meetingId: string;
  enabled?: boolean;
}

/** The detailed meeting payload (Meeting + Granola fields), as a loose record. */
export type GranolaMeetingDetail = Record<string, unknown>;

// granola-mcp-get-meetings returns an array of meeting entities
// ([{ base, type, platform, data }]); pull the first entity's data.
function parseFirstMeeting(raw: unknown): GranolaMeetingDetail | null {
  if (!Array.isArray(raw)) return null;
  const first = raw.find(
    (item) => item && typeof item === "object" && "data" in item,
  ) as { data?: GranolaMeetingDetail } | undefined;
  return first?.data ?? null;
}

export function useGranolaMeetingFetch({
  integrationConnectionId,
  meetingId,
  enabled = true,
}: UseGranolaMeetingFetchProps) {
  return useQuery({
    queryKey: ["granola-meeting", integrationConnectionId, meetingId],
    queryFn: async () => {
      if (!integrationConnectionId) {
        throw new Error("Missing integration connection ID");
      }
      const response = await orgAPI.toolbox.execute({
        integration_connection_id: integrationConnectionId,
        action_name: "granola-mcp-get-meetings",
        action_input: { meeting_ids: [meetingId] },
      });
      return parseFirstMeeting(response.data);
    },
    enabled: !!integrationConnectionId && !!meetingId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
